import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { recentsClient, type RecentEntityType } from "@/apis/recentsClient";

// Types for recents state
type RecentsByType = {
  device_usage_profile: number[];
};

type InitializedState = {
  device_usage_profile: boolean;
};

interface RecentsContextValue {
  recentsByType: RecentsByType;
  loading: boolean;
  initialized: InitializedState;
  loadRecentIds: (entityType: RecentEntityType, limit?: number) => Promise<void>;
  getRecentIds: (entityType: RecentEntityType) => number[];
  recordAccess: (entityType: RecentEntityType, entityId: number | string) => Promise<void>;
  removeRecent: (entityType: RecentEntityType, entityId: number | string) => Promise<void>;
  clearRecents: (entityType: RecentEntityType) => Promise<void>;
  isRecent: (entityType: RecentEntityType, entityId: number | string) => boolean;
}

const RecentsContext = createContext<RecentsContextValue | undefined>(undefined);

export const useRecents = (): RecentsContextValue => {
  const context = useContext(RecentsContext);
  if (!context) {
    throw new Error("useRecents must be used within a RecentsProvider");
  }
  return context;
};

interface RecentsProviderProps {
  children: ReactNode;
}

export const RecentsProvider = ({ children }: RecentsProviderProps): JSX.Element => {
  const [recentsByType, setRecentsByType] = useState<RecentsByType>({
    device_usage_profile: [],
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [initialized, setInitialized] = useState<InitializedState>({
    device_usage_profile: false,
  });

  // Load recent IDs for a specific entity type
  const loadRecentIds = useCallback(
    async (entityType: RecentEntityType, limit = 10): Promise<void> => {
      try {
        setLoading(true);
        const response = await recentsClient.getRecentEntityIds(entityType, limit);
        const recentIds = response.data || [];

        setRecentsByType((prev) => ({
          ...prev,
          [entityType]: recentIds,
        }));

        setInitialized((prev) => ({
          ...prev,
          [entityType]: true,
        }));
      } catch (error) {
        console.error(`Failed to load ${entityType} recents:`, error);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Get recent IDs for an entity type
  const getRecentIds = useCallback(
    (entityType: RecentEntityType): number[] => {
      return recentsByType[entityType] || [];
    },
    [recentsByType]
  );

  // Record access to an entity
  const recordAccess = useCallback(
    async (entityType: RecentEntityType, entityId: number | string): Promise<void> => {
      try {
        await recentsClient.recordAccess(entityType, entityId);

        // Update local state - move to front if exists, add if not
        const numericId = Number(entityId);
        setRecentsByType((prev) => {
          const currentList = prev[entityType] || [];
          const filtered = currentList.filter((id) => id !== numericId);
          return {
            ...prev,
            [entityType]: [numericId, ...filtered],
          };
        });
      } catch (error) {
        console.error("Failed to record recent access:", error);
      }
    },
    []
  );

  // Remove an entity from recents
  const removeRecent = useCallback(
    async (entityType: RecentEntityType, entityId: number | string): Promise<void> => {
      const numericId = Number(entityId);

      try {
        await recentsClient.removeRecent(entityType, entityId);

        setRecentsByType((prev) => ({
          ...prev,
          [entityType]: (prev[entityType] || []).filter((id) => id !== numericId),
        }));
      } catch (error) {
        console.error("Failed to remove recent:", error);
      }
    },
    []
  );

  // Clear all recents for an entity type
  const clearRecents = useCallback(async (entityType: RecentEntityType): Promise<void> => {
    try {
      await recentsClient.clearRecents(entityType);

      setRecentsByType((prev) => ({
        ...prev,
        [entityType]: [],
      }));
    } catch (error) {
      console.error("Failed to clear recents:", error);
    }
  }, []);

  // Check if an entity is in recents
  const isRecent = useCallback(
    (entityType: RecentEntityType, entityId: number | string): boolean => {
      const numericId = Number(entityId);
      return (recentsByType[entityType] || []).includes(numericId);
    },
    [recentsByType]
  );

  const value: RecentsContextValue = {
    recentsByType,
    loading,
    initialized,
    loadRecentIds,
    getRecentIds,
    recordAccess,
    removeRecent,
    clearRecents,
    isRecent,
  };

  return <RecentsContext.Provider value={value}>{children}</RecentsContext.Provider>;
};
